// src/scene/room.ts
//
// "A vast dim hall" — the environment the white wall is set into. There are no
// real lights in the scene (MeshBasicMaterial + ShaderMaterial only), so the
// room is built entirely from big, baked/gradient/shader planes whose job is to
// give the surrounding darkness STRUCTURE: a graded backdrop instead of flat
// black, side returns that frame the wall into an architectural recess, a far
// cornice glow up the climb, layered haze for volumetric depth, a present
// graded floor, and a camera-locked vignette that pools attention centre-screen.
//
// Coordinate facts (see wallLayout.ts / controls.ts): the wall is the plane
// X = 0 facing +X; the camera sits at +X (~4.6) looking straight toward −X,
// climbs in Y, Z is locked/centred across the wall width. So the room must
// wrap the −X side and the Z-edges; the back wall behind the viewer (+X) is
// rarely seen and is kept minimal.
//
// Every world-space surface here is fog-aware (the same pattern as
// wall.ts panelMaterial: merge THREE.UniformsLib.fog, include the fog chunks,
// defines USE_FOG/FOG_EXP2, fog:true) so it dissolves into the dark like the
// wall does. Decorative meshes set raycast = () => {} so they never steal art
// clicks. The vignette is screen-space (camera-locked) and intentionally NOT
// fogged.

import * as THREE from 'three';
import { FOCUS_UNIFORMS, FOCUS_DECL } from './focus';

/** Shared boilerplate for a fog-aware, world-space environment ShaderMaterial.
 *  `decl` holds global-scope declarations (uniforms — GLSL forbids these inside
 *  main); `body` is the fragment body that assigns `vec3 col` + `float alpha`. */
function fogPlane(opts: {
  uniforms?: Record<string, THREE.IUniform>;
  decl?: string;
  body: string;
  transparent?: boolean;
  blending?: THREE.Blending;
  depthWrite?: boolean;
  /** Spread the LIVE shared focus uniforms in by reference (merge clones, so
   *  they must be added after). Enables focusPool() in the shader body. */
  focus?: boolean;
}): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    fog: true,
    transparent: opts.transparent ?? false,
    depthWrite: opts.depthWrite ?? !(opts.transparent ?? false),
    blending: opts.blending ?? THREE.NormalBlending,
    defines: { USE_FOG: '', FOG_EXP2: '' },
    uniforms: {
      ...THREE.UniformsUtils.merge([THREE.UniformsLib.fog, opts.uniforms ?? {}]),
      ...(opts.focus ? FOCUS_UNIFORMS : {}),
    },
    vertexShader: /* glsl */ `
      #include <common>
      #include <fog_pars_vertex>
      varying vec2 vUvR;
      varying vec3 vWorld;
      void main() {
        vUvR = uv;
        vec4 wp = modelMatrix * vec4( position, 1.0 );
        vWorld = wp.xyz;
        vec4 mvPosition = viewMatrix * wp;
        gl_Position = projectionMatrix * mvPosition;
        #include <fog_vertex>
      }`,
    fragmentShader: /* glsl */ `
      #include <common>
      #include <fog_pars_fragment>
      varying vec2 vUvR;
      varying vec3 vWorld;
      ${opts.focus ? FOCUS_DECL : ''}
      ${opts.decl ?? ''}
      void main() {
        vec3 col; float alpha = 1.0;
        ${opts.body}
        gl_FragColor = vec4(col, alpha);
        #include <fog_fragment>
      }`,
  });
}

function decor<T extends THREE.Object3D>(o: T): T {
  o.raycast = () => {};
  return o;
}

export interface RoomOpts {
  /** Wall span across Z (WALL_WIDTH). */
  width: number;
  /** Total tall climb height (Y). */
  height: number;
}

/** The dim hall the wall lives in. Add once after buildWall(); a handful of big
 *  planes, no per-frame work. Tuned to stay DARK — it supports the art, never
 *  out-shouts it. */
export class Room {
  private godRayMats: THREE.ShaderMaterial[] = [];

  constructor(scene: THREE.Scene, opts: RoomOpts) {
    const { width, height } = opts;
    const cz = width / 2;                 // centre across the wall
    const top = height + 6;               // a touch above the climb
    const DEPTH = 26;                     // how far the room recedes behind the wall (−X)

    // ---- 1. Backdrop: a large graded plane well behind the wall (−X). ----
    // Gives the dark BEHIND/around the wall structure: cool near-black at the
    // floor and at the far Z-edges, a faint lift behind the wall centre so the
    // white wall reads as glowing out of a dim volume rather than a void. Sits
    // far enough back (and the fog is dense enough) that only its lifted core
    // is ever seen past the wall edges.
    const backdrop = decor(new THREE.Mesh(
      new THREE.PlaneGeometry(width * 4.5, top * 1.6),
      fogPlane({
        uniforms: { uTop: { value: top } },
        decl: 'uniform float uTop;',
        body: /* glsl */ `
          // vertical: lifted toward mid-height, falling to near-black at floor + ceiling
          float v = vUvR.y;
          float band = 1.0 - smoothstep(0.18, 0.62, abs(v - 0.46));
          // horizontal: lifted behind the wall centre, cool-black to the sides
          float h = 1.0 - smoothstep(0.16, 0.5, abs(vUvR.x - 0.5));
          float lift = band * h;
          // cool near-black base, lifting to a faint cool grey-blue core
          col = mix(vec3(0.026, 0.028, 0.035), vec3(0.10, 0.105, 0.125), lift * 0.9);
        `,
      }),
    ));
    backdrop.rotation.y = Math.PI / 2;     // face +X (toward the viewer)
    backdrop.position.set(-DEPTH, top * 0.5, cz);
    scene.add(backdrop);

    // ---- 2. Ceiling plane: dark, with a faint cornice glow where it meets
    // the top of the wall, implying a lit ceiling cove far up the climb. ----
    const ceiling = decor(new THREE.Mesh(
      new THREE.PlaneGeometry(DEPTH, width * 2.4),
      fogPlane({
        body: /* glsl */ `
          // world-keyed warm cove: brightest where the ceiling meets the wall
          // (x≈0), a thin glow that fades fast into ceiling shadow toward −X
          float toWall = 1.0 - smoothstep(0.0, 4.5, abs(vWorld.x));
          float glow = pow(toWall, 1.6) * 0.55;
          col = mix(vec3(0.018, 0.018, 0.024), vec3(0.085, 0.068, 0.046), glow);
        `,
      }),
    ));
    ceiling.rotation.x = Math.PI / 2;       // lie flat overhead, facing down
    ceiling.position.set(-DEPTH / 2 + 1.0, top - 0.5, cz);
    scene.add(ceiling);

    // ---- 3. Cornice glow strip: a soft horizontal warm gradient pinned just
    // above the top of the climb, an additive sliver implying the ceiling cove
    // light catching the wall head. ----
    const cornice = decor(new THREE.Mesh(
      new THREE.PlaneGeometry(width + 4, 5.0),
      fogPlane({
        transparent: true,
        blending: THREE.AdditiveBlending,
        body: /* glsl */ `
          // hot at the bottom edge (meets the wall top), fading up into the dark
          float g = smoothstep(0.0, 0.85, vUvR.y);   // 0 bottom → 1 top
          float warm = (1.0 - g);
          // soft horizontal falloff to the ends
          float hx = 1.0 - smoothstep(0.3, 0.5, abs(vUvR.x - 0.5));
          float a = warm * warm * hx;
          col = vec3(0.95, 0.78, 0.52);
          alpha = a * 0.28;
        `,
      }),
    ));
    cornice.rotation.y = Math.PI / 2;
    cornice.position.set(-0.4, top - 1.6, cz);
    cornice.renderOrder = -2;
    scene.add(cornice);

    // ---- 4. Side returns: two dark gradient planes at the wall's Z-edges,
    // receding toward −X, so the wall is clearly SET INTO a recess. Brightest
    // (least dark) where they meet the wall, dissolving to black as they go
    // back — reads as the shadowed side walls of the alcove. ----
    const returnDepth = DEPTH * 0.6;
    // Keyed off WORLD x (robust to the per-side Y rotation): brightest at the
    // wall contact (x≈0), dissolving to black as the return recedes to −X.
    const sideBody = /* glsl */ `
      float toWall = clamp(1.0 + vWorld.x / uReturnDepth, 0.0, 1.0); // 1 at x=0 → 0 at x=−depth
      float lit = smoothstep(0.0, 0.6, toWall);
      float v = 0.85 + 0.15 * vUvR.y;       // gentle vertical mottle, not a flat sheet
      col = mix(vec3(0.012, 0.013, 0.018), vec3(0.085, 0.088, 0.10), lit) * v;
    `;
    for (const side of [-1, 1]) {
      const ret = decor(new THREE.Mesh(
        new THREE.PlaneGeometry(returnDepth, top * 1.4),
        fogPlane({
          uniforms: { uReturnDepth: { value: returnDepth } },
          decl: 'uniform float uReturnDepth;',
          body: sideBody,
        }),
      ));
      // vertical plane spanning world X (depth) × Y (height), normal along ±Z,
      // placed just past the wall edge, receding from the wall (x≈0.3) to −X.
      ret.position.set(-returnDepth / 2 + 0.3, top * 0.5, side < 0 ? -0.4 : width + 0.4);
      ret.rotation.y = side < 0 ? 0 : Math.PI;   // face inward across Z
      scene.add(ret);
    }

    // ---- 5. Floor presence: a big graded polished-concrete plane UNDER the
    // existing blurred Reflector, extending it forward and giving the ground a
    // receding far edge + a faint long reflection-streak of the lit wall, so
    // the floor reads as a real receding surface instead of dissolving to
    // nothing. The Reflector keeps the soft moving highlight on top; this is
    // the tone bed beneath it. ----
    const floorLen = DEPTH;                 // along X (depth, toward the viewer)
    const floor = decor(new THREE.Mesh(
      new THREE.PlaneGeometry(floorLen, width * 2.2),
      fogPlane({
        uniforms: { uWidth: { value: width }, uLen: { value: floorLen } },
        decl: 'uniform float uWidth; uniform float uLen;',
        body: /* glsl */ `
          // world-space keyed (robust to the flat rotation):
          // depthNear = 1 at the wall contact line (x≈0), 0 out toward the viewer
          float depthNear = 1.0 - smoothstep(0.0, uLen * 0.65, vWorld.x);
          vec3 near = vec3(0.05, 0.052, 0.062);
          vec3 far  = vec3(0.013, 0.014, 0.019);
          col = mix(far, near, depthNear);
          // calm long reflection-streak of the lit wall along the centre of the
          // span, fading out into the room (kept faint — not a mirror)
          float zc = vWorld.z / max(uWidth, 0.001);     // 0..1 across the span
          float streak = 1.0 - smoothstep(0.0, 0.32, abs(zc - 0.5));
          col += vec3(0.05, 0.05, 0.058) * streak * depthNear * 0.7;
          // subtle long seams running into the room (grout lines across the span)
          float seam = smoothstep(0.985, 1.0, abs(sin(zc * uWidth * 0.9)));
          col -= vec3(0.012) * seam;
        `,
      }),
    ));
    floor.rotation.x = -Math.PI / 2;        // lie flat
    floor.position.set(floorLen / 2 - 0.6, -0.012, cz);  // just below the Reflector (y=0)
    floor.renderOrder = -3;                 // sits beneath the Reflector
    scene.add(floor);

    // ---- 6. Volumetric haze: 2 big, very faint vertical planes standing
    // between the camera and the wall, additive, to read as layered atmosphere
    // catching the dim light. Cheap depth cue; kept extremely subtle so the
    // mood stays dark and the art is never veiled. ----
    const hazeBody = /* glsl */ `
      // soft horizontal pool of haze, brightest low-centre, fading up + to sides
      float hx = 1.0 - smoothstep(0.1, 0.5, abs(vUvR.x - 0.5));
      float hy = smoothstep(1.0, 0.15, vUvR.y);   // denser low (floor haze)
      alpha = hx * hy;
    `;
    const hazeLayers: Array<{ x: number; a: number; c: THREE.Color }> = [
      { x: 1.4, a: 0.05, c: new THREE.Color(0.42, 0.40, 0.46) },
      { x: 2.8, a: 0.06, c: new THREE.Color(0.30, 0.30, 0.40) },
    ];
    for (const hl of hazeLayers) {
      const haze = decor(new THREE.Mesh(
        new THREE.PlaneGeometry(width * 1.8, top * 0.9),
        fogPlane({
          transparent: true,
          blending: THREE.AdditiveBlending,
          uniforms: { uColor: { value: hl.c }, uAmt: { value: hl.a } },
          decl: 'uniform vec3 uColor; uniform float uAmt;',
          body: /* glsl */ `
            ${hazeBody}
            col = uColor;
            alpha *= uAmt;
          `,
        }),
      ));
      haze.rotation.y = Math.PI / 2;        // face the viewer
      haze.position.set(hl.x, top * 0.34, cz);
      haze.renderOrder = -1;
      scene.add(haze);
    }

    // ---- 6b. Volumetric god-ray shafts (P2): faint additive light cones
    // descending from the implied ceiling spots onto the wall. One shaft per
    // baked light-pool centre up the climb (period 5.2, matching panelMaterial),
    // each a vertical quad standing just off the wall, facing the viewer. The
    // shader paints a soft cone — wide and hot at the top (the spot), tapering
    // and fading toward the floor — modulated by fog AND the focus pool so a
    // shaft glows brighter when the viewer is at its height. Animated breathing
    // keeps them alive. Kept very subtle: hazy shafts, not solid geometry. ----
    const PERIOD = 5.2;
    const SPOT0 = 1.3 + 0.42 * PERIOD;        // first pool centre (see wall.ts)
    const shaftH = PERIOD * 1.5;
    for (let yc = SPOT0; yc < height + 1.0; yc += PERIOD) {
      // two shafts per spot, offset across the wall width, for a raked look
      for (const zf of [0.34, 0.66]) {
        const mat = fogPlane({
          transparent: true,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          focus: true,
          uniforms: {
            uYC: { value: yc },
            uPhaseSeed: { value: yc * 0.7 + zf * 3.0 },  // unique breathing phase
            uIntensity: { value: 0.05 },                 // live, animated by update()
          },
          decl: 'uniform float uYC; uniform float uPhaseSeed; uniform float uIntensity;',
          body: /* glsl */ `
            // cone: hot narrow column at the top (spot mouth), widening + fading
            // toward the floor. vUvR.y: 0 bottom → 1 top.
            float top01 = vUvR.y;
            float cx = abs(vUvR.x - 0.5) * 2.0;          // 0 centre → 1 edge
            // shaft gets wider lower down: allowed half-width grows as we descend
            float halfW = mix(0.12, 0.46, 1.0 - top01);
            float across = 1.0 - smoothstep(halfW * 0.4, halfW, cx);
            // vertical: brightest near the top, long soft fade down
            float vert = smoothstep(0.0, 0.35, top01) * (0.25 + 0.75 * top01);
            float a = across * vert;
            // focus pool: a shaft at the viewer's height glows; others stay dim
            float fp = focusPool(vWorld.y);
            float lit = mix(0.45, 1.0, fp);
            col = vec3(1.0, 0.84, 0.58);                 // warm tungsten shaft
            alpha = a * lit * uIntensity;
          `,
        });
        this.godRayMats.push(mat);
        const shaft = decor(new THREE.Mesh(
          new THREE.PlaneGeometry(width * 0.5, shaftH),
          mat,
        ));
        shaft.rotation.y = Math.PI / 2;       // face the viewer (+X)
        shaft.position.set(0.9, yc, width * zf);
        shaft.renderOrder = -1;
        scene.add(shaft);
      }
    }

    // ---- 7. Vignette: now supplied by the post-processing pipeline
    // (postfx.ts VignetteGrainShader), so the old in-scene vignette plane is
    // removed to avoid double-darkening the corners. ----
  }

  private elapsed = 0;
  /** Per-frame: gently breathe the god-ray shaft intensities (each on its own
   *  phase) so the light shafts feel alive rather than static. dt in seconds. */
  update(dt: number): void {
    this.elapsed += dt;
    for (const mat of this.godRayMats) {
      const seed = mat.uniforms.uPhaseSeed.value as number;
      // slow breathing around a faint base — stays subtle (hazy light, not geo)
      mat.uniforms.uIntensity.value =
        0.05 + 0.02 * Math.sin(this.elapsed * 0.5 + seed);
    }
  }
}
